import React, { useEffect, useMemo, useState } from "react";
import rawEmbedScript from "./EmbedScript.html?raw";
import { Deployment } from "@/pages/Messaging/Deployments/Deployments";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface Props {
  show: boolean;
  onClose(): void;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      disabled={!text}
      className="text-xs font-medium px-3 py-1 rounded-lg border transition-colors disabled:opacity-40
        border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50"
      aria-label="Copy to clipboard"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

export default function DeployModal({ show, onClose }: Props) {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const api = useApi();
  const toastAlert = useToastAlert();

  useEffect(() => {
    if (!show) return;
    async function fetchDeployments() {
      try {
        const resp = await api.messaging.get("Deployment");
        if (!resp.ok) {
          toastAlert.displayToast(Severity.Error, "Failed to load deployments");
          return;
        }
        const data: Deployment[] = await resp.json();
        setDeployments(data);
        if (data.length > 0) setSelectedDeployment(data[0]);
      } catch {
        toastAlert.displayToast(Severity.Error, "Failed to load deployments");
      }
    }
    fetchDeployments();
  }, [show, api.messaging, toastAlert]);

  const embedScript = useMemo(() => {
    if (!selectedDeployment) return "";
    return rawEmbedScript
      .replace(/<!--[\s\S]*?-->\n?/g, "")
      .replace("{{ siteId }}", selectedDeployment.siteId.toString())
      .replace("{{ deploymentId }}", selectedDeployment.deploymentId.toString())
      .replace(
        "{{ scriptSrc }}",
        `${import.meta.env.VITE_MESSAGING_CLIENT_URL!}/messenger/embed.js`
      )
      .trim();
  }, [selectedDeployment]);

  const linkUrl = useMemo(() => {
    if (!selectedDeployment) return "";
    return `${import.meta.env.VITE_MESSAGING_CLIENT_URL!}/example_embed_wave.html?siteId=${selectedDeployment.siteId}&deploymentId=${selectedDeployment.deploymentId}`;
  }, [selectedDeployment]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Deploy messenger"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Deploy Messenger</h2>
              <p className="text-xs text-slate-400">Copy the embed code and add it to your website</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">

          {/* Deployment selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Deployment</label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              value={selectedDeployment?.id ?? ""}
              onChange={(e) => {
                const id = Number(e.currentTarget.value);
                setSelectedDeployment(deployments.find((d) => d.id === id) ?? null);
              }}
            >
              {deployments.length === 0 && <option value="">Loading…</option>}
              {deployments.map((d) => (
                <option key={d.id} value={d.id}>{d.displayName}</option>
              ))}
            </select>
          </div>

          {/* Direct link */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-600">Direct Link</label>
              <CopyButton text={linkUrl} />
            </div>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <input
                readOnly
                value={linkUrl}
                className="flex-1 bg-transparent text-sm text-slate-600 outline-none min-w-0"
              />
            </div>
          </div>

          {/* Embed code */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-600">Embed Code</label>
              <CopyButton text={embedScript} />
            </div>
            <div className="relative bg-slate-900 rounded-xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 pt-3 pb-2 border-b border-slate-700">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <span className="ml-2 text-xs text-slate-500 font-mono">embed.html</span>
              </div>
              <textarea
                readOnly
                value={embedScript}
                rows={7}
                className="w-full bg-transparent font-mono text-xs text-emerald-300 px-4 py-3 resize-none outline-none leading-relaxed"
                aria-label="Embed code"
              />
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-400">
            Paste the embed code before the <code className="bg-slate-200 px-1 rounded text-slate-600">&lt;/body&gt;</code> tag on every page.
          </p>
          <button
            onClick={onClose}
            className="text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
